import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Search, Upload, Bot, FileText, AlertTriangle, CheckCircle, Loader } from 'lucide-react';
import axios from 'axios';

const PlagiarismDetector = () => {
  const [activeTab, setActiveTab] = useState('web');
  const [content, setContent] = useState('');
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  const baseUrl = backendUrl.endsWith('/') ? backendUrl.slice(0, -1) : backendUrl;

  const handleWebCheck = async () => {
    if (!content.trim()) {
      setError('Please enter some text to check');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const res = await axios.post(`${baseUrl}/api/plagiarism/check-web`, {
        content,
        language: 'en'
      });
      setResult({ type: 'web', data: res.data });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to check plagiarism');
    } finally {
      setLoading(false);
    }
  };

  const handleAIDetection = async () => {
    if (!content.trim()) {
      setError('Please enter some text to analyze');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const res = await axios.post(`${baseUrl}/api/plagiarism/detect-ai`, {
        content,
        language: 'en'
      });
      setResult({ type: 'ai', data: res.data });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to detect AI content');
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentCompare = async () => {
    if (files.length < 2) {
      setError('Please upload at least 2 documents to compare');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });

      const res = await axios.post(`${baseUrl}/api/plagiarism/compare-docs`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setResult({ type: 'compare', data: res.data });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to compare documents');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length > 5) {
      setError('Maximum 5 files allowed');
      return;
    }
    setFiles(selectedFiles);
    setError(null);
  };

  const getScoreColor = (score) => {
    if (score < 30) return 'text-green-600';
    if (score < 70) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 font-sans p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center text-gray-500 hover:text-primary transition-colors font-medium text-sm">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
          </Link>
          <span className="font-heading font-bold text-lg text-primary tracking-tight">Plagiarism Detector</span>
        </div>

        {/* Title */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <FileText className="w-16 h-16 text-primary mx-auto mb-4" />
          <h1 className="text-4xl font-heading font-bold mb-4">Plagiarism & AI Detection</h1>
          <p className="text-gray-500 dark:text-gray-400 text-lg">Check originality, detect AI-written content, and compare documents</p>
        </motion.div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 bg-white dark:bg-gray-800 p-2 rounded-xl shadow-sm">
          <button
            onClick={() => { setActiveTab('web'); setResult(null); setError(null); }}
            className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
              activeTab === 'web' 
                ? 'bg-primary text-white shadow-md' 
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            <Search className="w-4 h-4" />
            Web Plagiarism
          </button>
          <button
            onClick={() => { setActiveTab('ai'); setResult(null); setError(null); }}
            className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
              activeTab === 'ai' 
                ? 'bg-primary text-white shadow-md' 
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            <Bot className="w-4 h-4" />
            AI Detection
          </button>
          <button
            onClick={() => { setActiveTab('compare'); setResult(null); setError(null); }}
            className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
              activeTab === 'compare' 
                ? 'bg-primary text-white shadow-md' 
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            <Upload className="w-4 h-4" />
            Document Compare
          </button>
        </div>

        {/* Content Area */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8">
          {/* Web Plagiarism Tab */}
          {activeTab === 'web' && (
            <div>
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Search className="w-5 h-5 text-primary" />
                Check for Web Plagiarism
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6 text-sm">
                Paste your text to search for similar content across the internet
              </p>
              
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Paste your text here to check for plagiarism..."
                className="w-full h-64 p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
              />
              
              <button
                onClick={handleWebCheck}
                disabled={loading || !content.trim()}
                className="mt-4 px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading ? <Loader className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                {loading ? 'Checking...' : 'Check Plagiarism'}
              </button>
            </div>
          )}

          {/* AI Detection Tab */}
          {activeTab === 'ai' && (
            <div>
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Bot className="w-5 h-5 text-primary" />
                Detect AI-Generated Content
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6 text-sm">
                Analyze if text was written by AI or a human
              </p>
              
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Paste text to check if it was written by AI..."
                className="w-full h-64 p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
              />
              
              <button
                onClick={handleAIDetection}
                disabled={loading || !content.trim()}
                className="mt-4 px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading ? <Loader className="w-4 h-4 animate-spin" /> : <Bot className="w-4 h-4" />}
                {loading ? 'Analyzing...' : 'Detect AI'}
              </button>
            </div>
          )}

          {/* Document Compare Tab */}
          {activeTab === 'compare' && (
            <div>
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Upload className="w-5 h-5 text-primary" />
                Compare Multiple Documents
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6 text-sm">
                Upload 2-5 documents (.txt, .docx, .pdf) to find similarities
              </p>
              
              <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center">
                <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <input
                  type="file"
                  multiple
                  accept=".txt,.docx,.pdf"
                  onChange={handleFileChange}
                  className="hidden"
                  id="file-upload"
                />
                <label
                  htmlFor="file-upload"
                  className="cursor-pointer inline-block px-4 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors"
                >
                  Choose Files
                </label>
                <p className="text-gray-500 mt-2 text-sm">Upload 2-5 documents (max 10MB each)</p>
              </div>

              {files.length > 0 && (
                <div className="mt-4 space-y-2">
                  <p className="font-medium text-sm">Selected Files:</p>
                  {files.map((file, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                      <FileText className="w-4 h-4" />
                      {file.name} ({(file.size / 1024).toFixed(1)} KB)
                    </div>
                  ))}
                </div>
              )}
              
              <button
                onClick={handleDocumentCompare}
                disabled={loading || files.length < 2}
                className="mt-4 px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading ? <Loader className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {loading ? 'Comparing...' : 'Compare Documents'}
              </button>
            </div>
          )}
        </div>

        {/* Error Display */}
        {error && (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-3"
          >
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-red-800 dark:text-red-300">{error}</p>
          </motion.div>
        )}

        {/* Results Display */}
        {result && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-8 bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8"
          >
            <h3 className="text-2xl font-bold mb-6 flex items-center gap-2">
              <CheckCircle className="w-6 h-6 text-green-600" />
              Analysis Complete
            </h3>

            {/* Web Plagiarism Results */}
            {result.type === 'web' && (
              <div className="space-y-6">
                <div className="text-center p-6 bg-gray-50 dark:bg-gray-900 rounded-lg">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Plagiarism Score</p>
                  <p className={`text-5xl font-bold ${getScoreColor(result.data.plagiarism_score)}`}>
                    {result.data.plagiarism_score}%
                  </p>
                  <p className="text-lg font-semibold mt-2">{result.data.verdict}</p>
                </div>

                <div>
                  <p className="text-gray-700 dark:text-gray-300 mb-4">{result.data.explanation}</p>
                </div>

                {result.data.sources_found.length > 0 && (
                  <div>
                    <h4 className="font-bold mb-3">Sources Found ({result.data.sources_found.length}):</h4>
                    <div className="space-y-3">
                      {result.data.sources_found.map((source, idx) => (
                        <div key={idx} className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                          <a href={source.link} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium">
                            {source.title}
                          </a>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{source.snippet}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* AI Detection Results */}
            {result.type === 'ai' && (
              <div className="space-y-6">
                <div className="text-center p-6 bg-gray-50 dark:bg-gray-900 rounded-lg">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">AI Probability Score</p>
                  <p className={`text-5xl font-bold ${getScoreColor(result.data.ai_score)}`}>
                    {result.data.ai_score}%
                  </p>
                  <p className="text-lg font-semibold mt-2">{result.data.verdict}</p>
                </div>

                <div>
                  <p className="text-gray-700 dark:text-gray-300 mb-4">{result.data.explanation}</p>
                </div>

                {result.data.markers && result.data.markers.length > 0 && (
                  <div>
                    <h4 className="font-bold mb-3">Detection Markers:</h4>
                    <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300">
                      {result.data.markers.map((marker, idx) => (
                        <li key={idx}>{marker}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Document Comparison Results */}
            {result.type === 'compare' && (
              <div className="space-y-6">
                <div className="text-center p-6 bg-gray-50 dark:bg-gray-900 rounded-lg">
                  <p className="text-lg font-semibold">{result.data.summary}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                    {result.data.documents_compared} documents analyzed
                  </p>
                </div>

                <div>
                  <h4 className="font-bold mb-3">Similarity Comparisons:</h4>
                  <div className="space-y-4">
                    {result.data.comparisons.map((comp, idx) => (
                      <div key={idx} className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-medium">{comp.doc1} ↔ {comp.doc2}</p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">{comp.verdict}</p>
                          </div>
                          <span className={`text-xl font-bold ${getScoreColor(comp.similarity)}`}>
                            {comp.similarity}%
                          </span>
                        </div>
                        
                        {comp.matching_sentences && comp.matching_sentences.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                            <p className="text-xs font-semibold text-gray-500 mb-2">Sample Matches:</p>
                            {comp.matching_sentences.slice(0, 2).map((match, mIdx) => (
                              <div key={mIdx} className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                                <p className="italic">" {match.from_doc1}..."</p>
                                <p className="text-green-600 dark:text-green-400 mt-1">✓ {match.similarity}% match</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default PlagiarismDetector;
